import React, { useCallback, useEffect } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { DataTypeProvider } from '@devexpress/dx-react-grid';
import { TableEditColumn } from '@devexpress/dx-react-grid-material-ui';
import { useFormikContext } from 'formik';
import IconButton from '@mui/material/IconButton';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import Tooltip from '@mui/material/Tooltip';
import TableCell from '@mui/material/TableCell';
import { AgentInputType } from '../../../../types/AgentInputType.types';
import Agentv2 from '../../../../types/Agentv2.types';
import getServicesByAgentID from '../../../../Apollo/queries/getServicesByAgentID';
import Table from '../../../../components/Table';
import columns from './components/columnDefenition';
import normalizeServicesById from './services/normalizeServicesById';
import getAllServicesQuery from '../../../../Apollo/queries/getAllServicesQuery';
import { AgentStatus } from '../../../../types/Agent.types';
import commitChanges from './commitChanges';
import removeAgentServiceQuery from '../../../../Apollo/mutations/removeAgentServiceQuery';
import DateEditor from './components/DateEditor';
import NumberEditor from './components/NumberEditor';
import TextEditor from './components/TextEditor';
import updateAgentServiceQuery from '../../../../Apollo/mutations/updateAgentServiceQuery';
import AlertMessage from '../../../../components/AlertMessage/AlertMessage';
import AgentServiceInit from '../../../../types/initValues/AgentService.init';
import { useLoadingContext } from '../../../../context/LoadingProvider/LoadingContext';
import TableSkeleton from '../../../../components/Table/TableSkeleton';
import Alert from '../../../../components/Alert';

const dateColumns = ['start_date', 'end_date'];
const numberColumns = ['agent_service_price'];
const textColumns = ['agent_service_description', 'customer_id'];

const BILLED_DELETE_TOOLTIP =
  'Bereits abgerechnete Leistungen können nicht mehr gelöscht werden. Details: ⓘ neben der Überschrift.';
const HISTORY_TOOLTIP =
  'Ausgelaufene und abgerechnete Leistungen sind Teil der Historie und können nicht mehr geändert werden — bei Bedarf neue Leistung anlegen. Details: ⓘ neben der Überschrift.';
const BILLED_START_DATE_TOOLTIP = 'Das Startdatum abgerechneter Leistungen kann nicht geändert werden';
const PRICE_EDIT_TOOLTIP =
  'Preisänderungen wirken ab dem nächsten Abrechnungslauf (dieser rechnet den Vormonat ab). Bereits erstellte Belege bleiben unverändert.';

interface ServiceDateEditorProps {
  value?: string;
  onValueChange: (value: string | null) => void;
  row?: { is_billed?: boolean };
  column?: { name: string };
}

/** start_date (auch Einmal-Leistung) bei abgerechneten Zeilen sperren; end_date bleibt offen. */
function ServiceDateEditor({ value, onValueChange, row, column }: ServiceDateEditorProps): JSX.Element {
  const isStartDateColumn = column?.name === 'start_date';
  const isEndDateColumn = column?.name === 'end_date';
  const lockStartDate = Boolean(isStartDateColumn && row?.is_billed);
  return (
    <DateEditor
      value={value}
      onValueChange={onValueChange}
      readOnly={lockStartDate}
      lockTooltip={lockStartDate ? BILLED_START_DATE_TOOLTIP : undefined}
      allowClear={isEndDateColumn}
    />
  );
}

interface ServiceNumberEditorProps {
  value?: string | number;
  onValueChange: (value: string) => void;
}

function ServiceNumberEditor({ value, onValueChange }: ServiceNumberEditorProps): JSX.Element {
  return <NumberEditor value={value} onValueChange={onValueChange} tooltip={PRICE_EDIT_TOOLTIP} />;
}

function DateTypeProvider() {
  return <DataTypeProvider editorComponent={ServiceDateEditor} for={dateColumns} />;
}

function NuberTypeProvider() {
  return <DataTypeProvider editorComponent={ServiceNumberEditor} for={numberColumns} />;
}

function TextTypeProvider() {
  return <DataTypeProvider editorComponent={TextEditor} for={textColumns} />;
}

const Provider = [DateTypeProvider, NuberTypeProvider, TextTypeProvider];

/**
 * Historisch ⇔ abgerechnet + gespeichertes end_date + Endmonat vor aktuellem Monat (Europe/Berlin).
 * Zeitzonenfest über Intl formatToParts (year/month by type) — kein positionsabhängiges slice auf format()-String
 * (small-ICU/en-US-Fallback würde sonst '8/27/2026' → '8/27/20' liefern).
 * Lexikographischer Monatsvergleich (YYYY-MM) — kein Date-Parse.
 * Funktioniert für 'YYYY-MM' und 'YYYY-MM-DD' identisch (slice(0, 7) nur auf end_date).
 */
function isHistoricalService(row: { is_billed?: boolean; end_date?: string | null }): boolean {
  if (!row.is_billed) {
    return false;
  }
  const rawEnd = row.end_date;
  if (rawEnd == null || rawEnd === '') {
    return false;
  }
  const endMonth = String(rawEnd).slice(0, 7);
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Europe/Berlin',
    year: 'numeric',
    month: '2-digit',
  }).formatToParts(new Date());
  const year = parts.find((p) => p.type === 'year')?.value ?? '';
  const month = parts.find((p) => p.type === 'month')?.value ?? '';
  const currentMonth = `${year}-${month}`;
  return endMonth < currentMonth;
}

function AgentServicesTable() {
  const { values } = useFormikContext<AgentInputType | Agentv2>();
  const { id } = values;

  const ServiceEditCell = useCallback((props: TableEditColumn.CellProps) => {
    const { row, children } = props;

    if (row.is_billed) {
      if (isHistoricalService(row)) {
        return (
          <TableCell style={{ textAlign: 'center' }}>
            <Tooltip title={HISTORY_TOOLTIP}>
              <span>
                <IconButton disabled>
                  <EditIcon />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title={HISTORY_TOOLTIP}>
              <span>
                <IconButton disabled>
                  <DeleteIcon />
                </IconButton>
              </span>
            </Tooltip>
          </TableCell>
        );
      }

      // Edit-Modus (commit/cancel): alle Commands unverändert durch Default-Cell
      const childArray = React.Children.toArray(children);
      const isEditMode = childArray.some(
        (child) => React.isValidElement(child) && (child.props.id === 'commit' || child.props.id === 'cancel'),
      );
      if (isEditMode) {
        // eslint-disable-next-line react/jsx-props-no-spreading
        return <TableEditColumn.Cell {...props} />;
      }

      // Anzeige-Modus: edit behalten, ausschließlich delete durch deaktivierten Papierkorb ersetzen
      const commands = React.Children.map(children, (child) => {
        if (!React.isValidElement(child) || child.props.id !== 'delete') {
          return child;
        }
        return (
          <Tooltip key="billed-delete" title={BILLED_DELETE_TOOLTIP}>
            <span>
              <IconButton disabled>
                <DeleteIcon />
              </IconButton>
            </span>
          </Tooltip>
        );
      });

      return <TableCell style={{ textAlign: 'center' }}>{commands}</TableCell>;
    }

    // eslint-disable-next-line react/jsx-props-no-spreading
    return <TableEditColumn.Cell {...props} />;
  }, []);
  const {
    data,
    refetch,
    loading: loadingServiceByAgentID,
    error: errorGetServicesByID,
  } = useQuery(getServicesByAgentID, { variables: { id } });
  const { data: allServices, loading: laodingAllServices, error: errorGetAllServices } = useQuery(getAllServicesQuery);
  const [
    deleteAgentService,
    {
      data: dataDeleteAgentService,
      error: errorDelteAgentService,
      loading: loadingRemoveAgentService,
      reset: resetDelete,
    },
  ] = useMutation(removeAgentServiceQuery);
  const [
    updateAgentService,
    {
      data: dataUpdateAgentService,
      error: errorUpdateAgentService,
      loading: loadingUpdateAgentService,
      reset: resetUpdate,
    },
  ] = useMutation(updateAgentServiceQuery);
  const { setLoading } = useLoadingContext();

  // CONTRACT R-1 (RITA-1390/1394): X-Klick/Auto-Hide muss den Apollo-Mutation-State zurücksetzen,
  // damit dieselbe Meldung nach dem Schließen erneut erscheinen kann.
  const handleCloseAlert = useCallback(() => {
    resetDelete();
    resetUpdate();
  }, [resetDelete, resetUpdate]);
  const editingStateColumnExtensions = [
    { columnName: 'id', editingEnabled: false },
    { columnName: 'service_default_price', editingEnabled: false },
    { columnName: 'service_title', editingEnabled: false },
    { columnName: 'service_type', editingEnabled: false },
    { columnName: 'service_tax', editingEnabled: false },
  ];

  setLoading(laodingAllServices || loadingServiceByAgentID || loadingRemoveAgentService || loadingUpdateAgentService);

  useEffect(() => {
    refetch();
  }, []);

  const row = data ? normalizeServicesById(data.getServicesByAgentID) : [AgentServiceInit];

  // Roh-Datumswerte VOR Normalisierung (YYYY-MM-Kürzung) — für 1:1-Durchreichung unveränderter Felder.
  const rawServiceDatesById: Record<string | number, { start_date: string | null; end_date: string | null }> = {};
  if (data?.getServicesByAgentID) {
    data.getServicesByAgentID.forEach(
      (item: { id?: number | string; start_date?: string | null; end_date?: string | null }) => {
        if (item != null && item.id != null) {
          rawServiceDatesById[item.id] = {
            start_date: item.start_date ?? null,
            end_date: item.end_date ?? null,
          };
        }
      },
    );
  }

  if (errorGetServicesByID || errorGetAllServices) {
    return <Alert />;
  }

  if (data && allServices) {
    return (
      <>
        <Table
          row={row}
          columns={columns}
          onCommitChanges={({ changed, deleted }) =>
            commitChanges({
              changed,
              deleted,
              rows: row,
              refetch,
              updateAgentService,
              deleteAgentService,
              rawServiceDatesById,
            })
          }
          columnExtensions={editingStateColumnExtensions}
          dataTypeProvider={Provider}
          editable={values.status_id !== AgentStatus.dismissed}
          showAddCommand
          showEditCommand
          showDeleteCommand
          EditCellForCommand={ServiceEditCell}
          defaultHiddenColumnNames={['agent_service_agent_id']}
          forServicesById={{
            tableName: 'AgentServiceTable',
            services: allServices.getAllServices,
            agent_id: id,
            refresh: refetch,
          }}
        />
        <AlertMessage
          typeMessage={(errorDelteAgentService || errorUpdateAgentService) && 'error'}
          alertMessage={
            errorDelteAgentService?.message || errorUpdateAgentService?.message || 'Es ist ein Fehler aufgetreten'
          }
          isAlertMessage={!!errorDelteAgentService || !!errorUpdateAgentService}
          handleChangeIsAlertMessage={handleCloseAlert}
        />
        <AlertMessage
          typeMessage={
            (dataDeleteAgentService && !errorDelteAgentService && 'success') ||
            (dataUpdateAgentService && !errorUpdateAgentService && 'success')
          }
          alertMessage={
            (dataDeleteAgentService && !errorDelteAgentService && 'Leistung wurde gelöscht') ||
            (dataUpdateAgentService && !errorUpdateAgentService && 'Leistung erfolgreich geändert')
          }
          isAlertMessage={
            (!!dataDeleteAgentService && !errorDelteAgentService) ||
            (!!dataUpdateAgentService && !errorUpdateAgentService)
          }
          handleChangeIsAlertMessage={handleCloseAlert}
        />
      </>
    );
  }
  return <TableSkeleton />;
}

export default AgentServicesTable;
